import React, { useEffect, useState, Suspense } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import PublicRouter from "./public-router";
import { publicRoute } from "./routes";
import { LoaderComponent } from "../shared/components/loaders/page-loader";
import { DrawerHeader } from "../shared/components/navigations/side-nav";
import { useTransactionCertificateStore } from "../store/transaction-certificates.store";
import { TCAppConfig } from "../shared/app-configs/tc-app-configs";
import { useApi } from "../shared/hooks/useApi";
import _ from 'lodash';
import { Typography } from "@mui/material";
import { isAdmin } from "../shared/utils/admin-utils";
import ProtectedAdminRoute from "../shared/components/protected-routes/protected-admin-route";
import { checkAdminStatus } from "../shared/service/admin.service";

const AuthRoute: React.FC = () => {
  const { cbLicensingCode, getUserInfo, setUserInfo, setCbLicensingCode, setOriginalAdminCBID, originalAdminCBID, isAdmin: isAdminFromStore, setIsAdmin, authToken } = useTransactionCertificateStore();
  const [loading, setLoading] = useState<boolean>(true);
  const [isNoEmail,setNoEmail] =  useState<boolean>(false);
  const { executeGet } = useApi();

  useEffect(() => {    
    // Fetch CB Licensing Code if it is not already set
    const fetchCBLicensingCode = async () => {
      try {
        // Assume getUserInfo fetches or returns the current user info
        const userInfo = await getUserInfo;
        
        if (!userInfo?.userEmail && !_.isEmpty(userInfo?.displayName)) {
          // User has no email
          setLoading(false);
          setNoEmail(true);
          return;
        }

        if (userInfo && userInfo?.userEmail) {
          // If we already have admin status in store and CB-ID, use that
          if (originalAdminCBID && cbLicensingCode) {
            if (isAdminFromStore && cbLicensingCode !== originalAdminCBID) {
              setCbLicensingCode(originalAdminCBID);
            }
            
            // If cbLicensingCode is not set at all, set it to originalAdminCBID
            if (!cbLicensingCode) {
              setCbLicensingCode(originalAdminCBID);
            }
            setLoading(false);
            return;
          }

          // First time login - fetch user's CB-ID
          const response: any = await executeGet(TCAppConfig.GET_CB_CO_CODE, {
            email: userInfo?.userEmail,
          });
          
          if (response?.status === 200 && response?.data?.length > 0) {
            const updatedUserInfo = { ...userInfo };
            setUserInfo(updatedUserInfo);
            
            // Set the user's CB-ID (assuming the first element's ID)
            const userCBId = response?.data[0]?.ID;
            
            // Set the CB Licensing Code
            setCbLicensingCode(userCBId);
            
            // Check if user is admin by calling the new API
            try {
              if (!authToken) {
                console.error('[AuthRoute] No auth token available to check admin status');
                setIsAdmin(false);
                setLoading(false);
                return;
              }

              const adminCheckResponse = await checkAdminStatus(userInfo.userEmail, authToken);
              
              if (adminCheckResponse?.status === 200 && adminCheckResponse?.data?.is_admin) {
                // User is admin
                setIsAdmin(true);
                setOriginalAdminCBID(userCBId);
              } else {
                // User is not admin
                setIsAdmin(false);
              }
            } catch (error) {
              console.error('[AuthRoute] Error checking admin status:', error);
              setIsAdmin(false);
            }
            
            setLoading(false);
          } else {
            // No CB-ID found
            setLoading(false);
          }
        }
      } catch (error) {
        setLoading(false);
      }
    };

    fetchCBLicensingCode();
  }, []);

  // if(!loading && isNoEmail){
  //   return(
  //     <LoaderComponent error = {true} errorMsg="Since you donot have a valid email, you donot have access to the application"/>
  //   )
  // } else -  to be uncommented when user email are added in data
    if (loading || !cbLicensingCode) {
    // Show loader while fetching data
    return <LoaderComponent />;
  }
   
  // Check admin status from store
  const userIsAdmin = isAdmin();


  return (
    <Suspense fallback={<><DrawerHeader /><LoaderComponent /></>}>
      <Routes>
        {/* If user is admin, redirect to admin dashboard, otherwise to home */}
        <Route path="/" element={<Navigate to={userIsAdmin ? "/admin" : "/home"} replace />} />
        <Route element={<PublicRouter />}>
          {publicRoute?.map(({ path, id, component: Component, requiresAdmin }) => (
            <Route 
              key={id} 
              path={path} 
              element={
                requiresAdmin ? (
                  <ProtectedAdminRoute>
                    <Component />
                  </ProtectedAdminRoute>
                ) : (
                  <Component />
                )
              }
            />
          ))}
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AuthRoute;